/**
 * Структурированные инструкции по ролям. Источники:
 *  - Документация/ИНСТРУКЦИЯ_СТУДЕНТ.md
 *  - Документация/ИНСТРУКЦИЯ_ПРЕПОДАВАТЕЛЬ.md
 *  - Документация/ИНСТРУКЦИЯ_РОЛИ.md
 *
 * Каждый блок:
 *  purpose: список из 3–10 пунктов «зачем эта роль»
 *  login: краткий протокол входа (массив шагов)
 *  screens: главные экраны, [{ name, purpose }]
 *  scenarios: типичные сценарии, [{ title, steps: [string|{step,note}] }]
 *  faq: частые вопросы, [{ q, a }]
 *  contacts: специфические контакты роли (опц.)
 */

export const COMMON_PRINCIPLES = {
  login: [
    'Логин: ИНН (14 цифр) либо корпоративный e-mail.',
    'Пароль: минимум 8 символов; смена через «Профиль → Безопасность».',
    'ПИН-код: 4 цифры — подтверждает юридически значимые действия (подписание ведомостей, заявлений, договоров).',
    'Для ролей sysadmin / rectorate / accountant / archivist 2FA обязательна (TOTP).',
    'Восстановление пароля: /recover-password/ — ввод ИНН + e-mail → ссылка на 4 часа.',
    '5 неудачных попыток ввода 2FA-кода за 10 минут → блокировка на 30 минут.',
  ],
  layout: [
    'Сайдбар (слева) — навигация по разделам.',
    'Топбар — поиск (Ctrl+K), смена темы, языка, уведомления, профиль.',
    'Нижняя навигация — мобильные ≤768px, 5 ключевых разделов роли.',
    'Дашборд («Главная») — сводка-виджеты по роли.',
    'Чат `/chats/` — личные и групповые переписки.',
    'Профиль `/personalcard/` — личные данные, документы, Europass.',
  ],
  languages: 'Русский (по умолчанию), кыргызча, English. Переключение в топбаре. <html lang> синхронизируется автоматически.',
}

export const ROLE_INSTRUCTIONS = {
  // ───────── Учебный процесс ─────────

  student: {
    purpose: [
      'Видеть расписание и журнал посещаемости',
      'Получать темы и материалы лекций, выполнять задания',
      'Сдавать онлайн-тесты и участвовать в электронных экзаменах',
      'Видеть оценки по всем дисциплинам и итоговый средний балл (GPA)',
      'Подавать электронные заявления (пересдача, академический отпуск, справки)',
      'Проверять оплату обучения и долги по договору',
      'Бронировать место в общежитии, работать с библиотекой',
      'Вести ВКР и работать с карьерным порталом (если применимо)',
    ],
    login: [
      'Откройте https://edoc.chu.kg.',
      'Введите ИНН (14 цифр) в поле «Логин».',
      'Введите пароль (минимум 8 символов, выдан при зачислении).',
      'Введите ПИН-код (4 цифры) — подтверждает чувствительные действия.',
      'Если забыли пароль: «Восстановить пароль» → ИНН + e-mail → ссылка 4 часа.',
      'Если забыли ПИН: обратитесь в деканат лично с паспортом.',
    ],
    screens: [
      { name: 'Главная', purpose: 'Сводка: пары на сегодня, последние оценки, уведомления, баланс по оплате' },
      { name: 'Расписание', purpose: 'Недельная сетка пар с аудиторией, преподавателем и зданием' },
      { name: 'Дисциплины', purpose: 'Список дисциплин текущего семестра + темы и материалы по каждой' },
      { name: 'Журнал', purpose: 'Посещаемость и оценки по каждой дисциплине' },
      { name: 'Тесты и работы', purpose: 'Активные задания и тесты, статус сдачи, дедлайны' },
      { name: 'Экзамены', purpose: 'Расписание сессии, ведомость, прокторинг' },
      { name: 'Заявления', purpose: 'Подача и отслеживание заявлений' },
      { name: 'Оплата', purpose: 'Договор, начисления, история платежей, генерация квитанции' },
      { name: 'Библиотека', purpose: 'Поиск книг, выдачи, продление, штрафы' },
      { name: 'Общежитие', purpose: 'Заявка на место, история проживания' },
      { name: 'ВКР', purpose: 'Тема, научный руководитель, версии работы, антиплагиат' },
      { name: 'Карьера', purpose: 'Резюме, вакансии, заявки в компании' },
    ],
    scenarios: [
      {
        title: 'Посмотреть расписание на неделю',
        steps: [
          'Перейдите в «Расписание». По умолчанию открыта текущая неделя.',
          'Стрелками ←/→ переключайте недели.',
          'Клик по паре → детали: преподаватель, тема, ссылка на онлайн-кабинет (если пара дистанционная).',
        ],
      },
      {
        title: 'Сдать тест',
        steps: [
          'Откройте «Тесты и работы → активный тест».',
          'Прочитайте инструкцию (количество вопросов, время, число попыток).',
          'Нажмите «Начать». Таймер не останавливается — не закрывайте вкладку.',
          'После последнего вопроса — «Завершить». Результат сразу либо после ручной проверки.',
        ],
      },
      {
        title: 'Сдать задание (загрузить файл)',
        steps: [
          'Найдите задание со статусом «Принимается» в «Тесты и работы».',
          'Нажмите «Загрузить решение» → выберите файл (PDF/DOCX/ZIP до 50 МБ).',
          'Введите комментарий → подтвердите ПИН-кодом.',
          'Статус меняется на «На проверке». Преподаватель оставит оценку и комментарий.',
        ],
      },
      {
        title: 'Подать заявление',
        steps: [
          'Откройте «Заявления → Создать».',
          'Выберите тип: пересдача, академ.отпуск, справка с места учёбы, перевод.',
          'Заполните форму, при необходимости приложите документы.',
          'Подтвердите ПИН-кодом. Статус — в «Мои заявления».',
        ],
      },
      {
        title: 'Проверить и оплатить обучение',
        steps: [
          'Откройте «Оплата». Видите сумму договора, начисления, историю, остаток.',
          '«Сформировать квитанцию» → PDF с реквизитами.',
          'Платежи через банк подтягиваются автоматически за 1–2 рабочих дня.',
        ],
      },
      {
        title: 'Записаться в общежитие',
        steps: [
          '«Общежитие → Подать заявку».',
          'Заполните анкету (доход семьи, семейное положение, льготы).',
          'Приложите документы. Решение комиссии придёт на e-mail и в систему.',
        ],
      },
      {
        title: 'Ведение ВКР (выпускной курс)',
        steps: [
          'В разделе «ВКР» виден научный руководитель и тема.',
          'Загружайте версии работы (черновики и финальная).',
          'Каждая версия проходит автоматическую проверку на плагиат.',
          'Финальная версия — не позднее, чем за 21 день до защиты.',
        ],
      },
    ],
    faq: [
      { q: 'Не вижу пары в расписании', a: 'Возможно, расписание ещё не утверждено методистом. Проверьте через 24 часа или обратитесь в деканат.' },
      { q: 'Оценка выставлена неправильно', a: 'Напишите преподавателю через «Чат». Если не решено — заявление на апелляцию.' },
      { q: 'Не могу загрузить задание — слишком большой файл', a: 'Лимит 50 МБ. Сжимайте PDF или разбивайте ZIP.' },
      { q: 'Платёж не отображается', a: 'Банковские платежи поступают 1–2 рабочих дня. Если прошло больше 3 дней — в бухгалтерию с квитанцией.' },
      { q: 'Тест прервался из-за интернета', a: 'Если разрешена повторная попытка — она доступна через 5 минут. Иначе — к преподавателю со скриншотом.' },
      { q: 'Не приходят уведомления на e-mail', a: 'Проверьте «Настройки → Уведомления» и папку «Спам».' },
      { q: 'Хочу включить 2FA', a: '«Настройки → Безопасность → Включить 2FA» → установите Authenticator → сканируйте QR.' },
      { q: 'Что делать при потере телефона с 2FA', a: 'В деканат с паспортом — 2FA отключит сотрудник ОК.' },
    ],
  },

  teacher: {
    purpose: [
      'Видеть расписание своих пар на семестр',
      'Вести электронный журнал посещаемости и оценок (горячие клавиши 1–4)',
      'Выкладывать темы и материалы лекций',
      'Создавать задания, тесты и самостоятельные работы',
      'Проверять загруженные студентами решения',
      'Использовать конструктор тестов и банк вопросов кафедры',
      'Формировать и подписывать экзаменационные ведомости',
      'Запускать проверку работ через сервис антиплагиата',
      'Видеть учебную нагрузку (часы, аудиторные/внеаудиторные)',
      'Вести научную работу: публикации, гранты, конференции',
    ],
    login: [
      'Откройте https://edoc.chu.kg.',
      'ИНН + пароль + ПИН-код — выдаются ОК при приёме на работу.',
      'Если 2FA включена — введите 6-значный код Authenticator.',
      'ПИН требуется при выставлении итоговых оценок, подписании ведомости.',
    ],
    screens: [
      { name: 'Главная', purpose: 'Сводка: пары на сегодня, задания на проверке, уведомления' },
      { name: 'Расписание', purpose: 'Личное расписание на семестр и неделю' },
      { name: 'Журнал', purpose: 'Журналы по всем дисциплинам и группам' },
      { name: 'Темы и материалы', purpose: 'Календарно-тематический план, прикреплённые файлы' },
      { name: 'Задания', purpose: 'Конструктор и контроль выполнения заданий' },
      { name: 'Тесты', purpose: 'Конструктор тестов, банк вопросов кафедры' },
      { name: 'Ведомости', purpose: 'Экзаменационные ведомости. Статус: Черновик / Подписано / Сдано' },
      { name: 'Плагиат', purpose: 'Очередь проверок, отчёты по студенческим работам' },
      { name: 'Нагрузка', purpose: 'План и факт нагрузки, % выполнения' },
      { name: 'Наука', purpose: 'Публикации, гранты, конференции' },
    ],
    scenarios: [
      {
        title: 'Заполнить журнал посещаемости (горячие клавиши)',
        steps: [
          '«Журнал → дисциплина → группа», выберите дату пары.',
          '1 — был; 2 — отсутствовал по уважит.; 3 — без уважит. (н/б); 4 — опоздал.',
          'Стрелка ↓ — следующий студент.',
          '«Сохранить» (или Ctrl+S). Покажет % посещаемости.',
        ],
      },
      {
        title: 'Выставить оценку',
        steps: [
          'В журнале клик по ячейке (студент × дата) → ввод оценки.',
          'Введите число 1–5 или буквенную (A/B/C/D/F) и комментарий.',
          'Enter — подтверждение. Изменение задним числом — в аудит-журнале.',
        ],
      },
      {
        title: 'Загрузить тему лекции и материалы',
        steps: [
          '«Темы и материалы → дисциплина → нужная неделя → Добавить тему».',
          'Введите название, описание, прикрепите файлы (drag-n-drop).',
          'Можно добавить ссылку на видео.',
          'Сохраните — материал моментально виден студентам.',
        ],
      },
      {
        title: 'Создать задание',
        steps: [
          '«Задания → Создать»: название, описание, тип (эссе/задача/лаб./проект).',
          'Дедлайн и поздняя сдача (штраф %), вес в итоговой оценке.',
          'Допустимые форматы файлов и размер, группы получателей.',
          'Сохраните — студенты увидят задание.',
        ],
      },
      {
        title: 'Создать тест',
        steps: [
          '«Тесты → Создать»: параметры (название, время, число вопросов, попытки).',
          'Вопросы: вручную или из «Банка вопросов» (фильтры по теме/сложности).',
          'Типы: одиночный/множественный выбор, число, строка, открытый ответ.',
          'Назначьте группам, установите окно доступа (даты с–по).',
          'После публикации студенты видят тест.',
        ],
      },
      {
        title: 'Сформировать ведомость',
        steps: [
          '«Ведомости → дисциплина + группа».',
          'Система подтянет средневзвешенные оценки. Скорректируйте при необходимости.',
          '«Подписать» → введите ПИН.',
          'Ведомость уходит зав. кафедрой → деканат.',
        ],
      },
      {
        title: 'Запустить проверку на плагиат',
        steps: [
          '«Плагиат → Загрузить работу» либо в карточке ВКР/курсовой — «Проверить».',
          'Отчёт за 5–30 минут: источники, % уникальности, риск-балл.',
        ],
      },
      {
        title: 'Внести публикацию (наука)',
        steps: [
          '«Наука → Публикации → Добавить».',
          'Автор(ы), название, журнал, год, ISSN/ISBN, ссылка/DOI, тип.',
          'Прикрепите PDF. Используется в отчётах кафедры и рейтингах.',
        ],
      },
    ],
    faq: [
      { q: 'Не открывается журнал — нет прав', a: 'Журналы привязаны к расписанию. Если пара не закреплена за вами — свяжитесь с методистом.' },
      { q: 'Не сохранил оценку — кнопка серая', a: 'Проверьте корректность значения. Допустимый диапазон зависит от настройки дисциплины.' },
      { q: 'Студент не видит мою лекцию', a: 'Проверьте, что выбрана правильная группа и файл загрузился (зелёная галочка).' },
      { q: 'Не могу подписать ведомость', a: 'Не все оценки выставлены или есть студенты в академотпуске. Деканат вернёт с пояснением.' },
      { q: 'Тест считает неправильный ответ как правильный', a: 'Проверьте корректный вариант ответа. Прежние ответы можно пересчитать.' },
      { q: 'Сервис плагиата недоступен', a: 'Отчёты в очереди. Проверьте через час.' },
      { q: 'Изменился номер аудитории — кто меняет?', a: 'Расписание меняет методист. Заявка через «Чат → Методист».' },
    ],
    contacts: [
      'IT-отдел: +996 (XXX) XXX-XXX, доб. 101',
      'Методический отдел: +996 (XXX) XXX-XXX, доб. 102',
      'Зав. кафедрой: контакт в карточке кафедры (раздел «Структура»).',
    ],
  },

  curator: {
    purpose: [
      'Все возможности преподавателя',
      'Детализация успеваемости и посещаемости группы (curator-drilldown)',
      'Заметки о внеучебной активности студентов группы',
      'Приём оправдательных документов (academic absence)',
    ],
    login: [
      'Логин = ИНН + пароль + ПИН (как у преподавателя).',
      'Куратор — преподаватель, дополнительно ведущий студенческую группу.',
    ],
    screens: [
      { name: 'Детализация по группе', purpose: 'Глубокий анализ группы (отсев, успеваемость, по каждому студенту)' },
      { name: 'Студенческая жизнь', purpose: 'Заметки о внеучебной активности студентов' },
      { name: 'Оправдательные документы', purpose: 'Приём справок (медицинские, военкомат)' },
    ],
    scenarios: [
      {
        title: 'Найти студентов под угрозой отсева',
        steps: [
          'На главной куратора — виджет «Студенты под угрозой отсева» (>3 двоек или >30% пропусков).',
          'Клик по студенту → /curator-drilldown/?student=<id>.',
          'Создать обращение к декану через «Заявления → служебная записка».',
        ],
      },
    ],
    faq: [
      { q: 'Меня назначили куратором — где увидеть закреплённую группу?', a: 'На главной → виджет «Моя группа» + раздел «Студенты» в боковом меню.' },
    ],
  },

  head_of_department: {
    purpose: [
      'Все возможности преподавателя',
      'Карточки преподавателей кафедры (просмотр)',
      'Нагрузка кафедры по семестрам',
      'Учебные планы (просмотр и согласование)',
      'Все журналы и ведомости кафедры',
      'Дашборд (KPI кафедры)',
    ],
    login: ['ИНН + пароль + ПИН.'],
    screens: [
      { name: 'Сотрудники', purpose: 'Карточки преподавателей кафедры' },
      { name: 'Нагрузка', purpose: 'Распределение часов по преподавателям' },
      { name: 'Учебные планы', purpose: 'Просмотр и согласование ОПОП' },
      { name: 'Дашборд', purpose: 'KPI кафедры' },
    ],
    scenarios: [
      {
        title: 'Утвердить нагрузку в начале семестра',
        steps: [
          '«Нагрузка» → фильтр по кафедре.',
          'Проверка часов по каждому преподавателю.',
          'Согласование (если расхождений нет) либо отправка методисту на пересчёт.',
        ],
      },
    ],
    faq: [
      { q: 'Не вижу нагрузку преподавателя X', a: 'Проверьте, что преподаватель закреплён за вашей кафедрой в Структуре.' },
    ],
  },

  dean: {
    purpose: [
      'Все возможности зав. кафедрой',
      'Дашборд факультета: контингент, средний балл, отсев, защиты',
      'Утверждение учебных планов кафедр',
      'Полный доступ к админ-панели',
      'Данные для аккредитации (ECAQA/IAAR)',
      'Приказы по факультету (создание, подписание)',
    ],
    login: ['ИНН + пароль + ПИН + 2FA рекомендуется.'],
    screens: [
      { name: 'Дашборд факультета', purpose: 'KPI факультета' },
      { name: 'Утверждение УП', purpose: 'Согласование учебных планов кафедр' },
      { name: 'Аккредитация', purpose: 'Метрики ECAQA / IAAR' },
      { name: 'Приказы', purpose: 'Приказы по факультету' },
    ],
    scenarios: [
      {
        title: 'Утвердить ОПОП',
        steps: [
          '/syllabus-approval/ → план в статусе «Согласовано кафедрой».',
          'Проверка структуры (общие компетенции, выпускные, часы).',
          '«Утвердить» с подписанием ПИН-кодом.',
          'План становится активным с следующего семестра.',
        ],
      },
    ],
    faq: [
      { q: 'Кто может подписать приказ', a: 'Декан + ректорат. Цифровая подпись через ПИН.' },
    ],
  },

  methodist: {
    purpose: [
      'Конструктор расписания (drag&drop пар по сетке)',
      'Импорт расписания из XLSX/CSV',
      'Конструктор учебных планов (семестры, дисциплины, компетенции)',
      'Сравнение версий ОПОП',
      'Управление сессиями и пересдачами',
      'Учёт учебных групп',
      'Государственные отчёты (Form 1-НК, Контингент)',
    ],
    login: ['ИНН + пароль + ПИН + 2FA рекомендуется.'],
    screens: [
      { name: 'Конструктор расписания', purpose: 'Drag&Drop пар' },
      { name: 'Конструктор УП', purpose: 'Семестры, дисциплины, компетенции' },
      { name: 'Сравнение ОПОП', purpose: 'Diff между версиями' },
      { name: 'Сессии', purpose: 'Управление сессиями и пересдачами' },
      { name: 'Группы', purpose: 'Учёт учебных групп' },
      { name: 'Отчёты', purpose: 'Государственные отчёты' },
    ],
    scenarios: [
      {
        title: 'Составить расписание на семестр',
        steps: [
          '/groups/ → проверить актуальность списка групп.',
          '/syllabuses/ → утверждённые планы.',
          '/schedule-builder/ → перетащить дисциплины из плана на сетку недели.',
          'Проверка конфликтов (преподаватель в двух местах одновременно — красным).',
          '«Опубликовать» → расписание видимо студентам и преподавателям.',
        ],
      },
    ],
    faq: [
      { q: 'Какой формат файла для импорта расписания', a: 'XLSX/CSV, шаблон на /schedule-import/. Колонки: дата/группа/дисциплина/преподаватель/аудитория/тип.' },
    ],
  },

  // ───────── Кадры ─────────

  hr_students: {
    purpose: [
      'Единый реестр студентов с фильтрами',
      'Приёмная кампания (мастер из 4 шагов)',
      'Переводы (между группами, формами, на следующий курс)',
      'Академический отпуск, отчисление, восстановление, выпуск',
      'Приказы по студентам',
      'Иностранные студенты (визы, миграция, регистрация)',
      'Воинский учёт',
      'Аспиранты, магистранты, слушатели ДПО',
      'Внеучебная активность',
      'Общежитие',
    ],
    login: ['ИНН + пароль + ПИН + 2FA рекомендуется.'],
    screens: [
      { name: 'Реестр студентов', purpose: 'Фильтры: направление, курс, статус' },
      { name: 'Приёмная кампания', purpose: 'Мастер зачисления' },
      { name: 'Переводы', purpose: 'Между группами/формами/курсами' },
      { name: 'Жизненный цикл студента', purpose: 'Академ.отпуск, отчисление, восстановление, выпуск' },
    ],
    scenarios: [
      {
        title: 'Принять нового студента',
        steps: [
          '/admission/ → «Зачислить».',
          'Шаг 1: ФИО, ИНН, паспорт.',
          'Шаг 2: телефон, e-mail, адрес.',
          'Шаг 3: факультет, направление, группа, курс, форма, финансирование, язык.',
          'Шаг 4: проверка → «Принять».',
          'Логин/пароль создаются автоматически и отправляются на e-mail.',
          'Студент при первом входе меняет пароль.',
        ],
      },
    ],
    faq: [
      { q: 'Студент потерял пароль до первого входа', a: 'В реестре найти, «Сбросить пароль» — отправит ссылку на e-mail.' },
    ],
  },

  hr_employees: {
    purpose: [
      'Реестр сотрудников',
      'Мастер найма (4 шага: данные → должность → доступы → финал)',
      'Карточка сотрудника (14 разделов)',
      'Кадровые приказы',
      'Оргструктура (управления, отделы, должности)',
    ],
    login: ['ИНН + пароль + ПИН + 2FA рекомендуется.'],
    screens: [
      { name: 'Реестр сотрудников', purpose: 'Поиск и фильтры' },
      { name: 'Мастер найма', purpose: '4 шага оформления' },
      { name: 'Карточка сотрудника', purpose: 'Личное, паспорт, образование, военный, семья, награды и т.п.' },
    ],
    scenarios: [
      {
        title: 'Приём преподавателя',
        steps: [
          '/employee-wizard/ → шаг 1: ФИО, паспорт, ИНН, телефон.',
          'Шаг 2: кафедра, должность, ставка, дата начала.',
          'Шаг 3: e-mail, временный пароль (генерируется), роль (teacher), 2FA по необходимости.',
          'Шаг 4: проверка → «Принять на работу».',
          'Приказ формируется автоматически и уходит на подписание.',
        ],
      },
    ],
    faq: [
      { q: 'Сотрудник переходит на другую кафедру', a: 'Карточка → «Перевести» → новая кафедра/должность → формируется приказ о переводе.' },
    ],
  },

  // ───────── Финансы и техническая работа ─────────

  accountant: {
    purpose: [
      'Приходные/расходные кассовые ордера (форма по ГКУД)',
      'Контракты студентов: суммы, льготы, рассрочки',
      'Зарплаты: оклад, надбавки, удержания, премии, отпускные, командировочные',
      'Фонды и счета',
      'Расходы / поступления, аналитика по периодам',
      'Приём оплаты за общежитие',
    ],
    login: [
      'ИНН + пароль + ПИН.',
      '2FA ОБЯЗАТЕЛЬНА (TOTP).',
    ],
    screens: [
      { name: 'Бухгалтерия', purpose: 'Основной модуль' },
      { name: 'Бухгалтерская аналитика', purpose: 'Отчёты для ректора и ФНС' },
    ],
    scenarios: [
      {
        title: 'Начислить зарплату за месяц',
        steps: [
          '«Бухгалтерия» → «Сотрудники» → выбор месяца.',
          'Проверка табеля (/report-card/ подтягивает часы).',
          'Расчёт: оклад + надбавки − удержания.',
          'Формирование ведомости → подпись ПИН-кодом главного бухгалтера.',
          'Выгрузка в банк (XLSX).',
        ],
      },
    ],
    faq: [
      { q: 'Студент перевёл деньги, не вижу в системе', a: '1–2 рабочих дня для подтягивания. Иначе — звонить в банк.' },
    ],
  },

  archivist: {
    purpose: [
      '3 архива: университет, студенты, сотрудники',
      'Иерархия: Фонды → Описи → Дела → Документы',
      'OCR-поиск по тексту отсканированных документов',
      'Связывание приказов с архивными делами',
      'Журнал действий (все операции логируются)',
    ],
    login: ['ИНН + пароль + ПИН + 2FA ОБЯЗАТЕЛЬНА.'],
    screens: [
      { name: 'Архив', purpose: '3 архива с разными типами' },
      { name: 'OCR-поиск', purpose: 'Полнотекстовый поиск по сканам' },
      { name: 'Связь приказов', purpose: 'Привязка цифровых приказов к делам' },
    ],
    scenarios: [
      {
        title: 'Оцифровка дела',
        steps: [
          '/archive/ → нужное дело → «Оцифровать».',
          'Загрузка PDF/JPG-сканов → запуск OCR.',
          'Проверка распознанного текста.',
          'Подтверждение → дело помечается «Оцифровано».',
          'Текст становится доступен в OCR-поиске.',
        ],
      },
    ],
    faq: [
      { q: 'Срок хранения документа', a: 'Указан в карточке дела. Личные дела студентов и сотрудников — 75 лет. Административные документы — постоянное хранение.' },
    ],
  },

  dorm_staff: {
    purpose: [
      'Корпуса, комнаты, места',
      'Заселение / выселение студентов',
      'Учёт оплаты проживания',
      'Инциденты (поломки, нарушения)',
    ],
    login: ['ИНН + пароль + ПИН.'],
    screens: [
      { name: 'Общежитие', purpose: 'Корпуса, комнаты, заселения, инциденты' },
    ],
    scenarios: [
      {
        title: 'Заселение по приказу',
        steps: [
          '/dorm/ → «Заселения» → «Новое».',
          'Выбор студента, корпуса, комнаты, периода.',
          'Договор № + ежемесячная плата.',
          'Сохранение → автоматическое начисление в бухгалтерии.',
        ],
      },
    ],
    faq: [
      { q: 'Студент потерял ключ', a: 'Зафиксируйте инцидент в /dorm/ → «Инциденты». Бухгалтерия начислит штраф.' },
    ],
  },

  proctor: {
    purpose: [
      'Контроль академической честности на онлайн-экзаменах',
      'Просмотр видеопотока студента (вебкамера + screen)',
      'Алерты (взгляд в сторону, голос постороннего, чужие окна)',
      'Ручная пометка «Подозрение»',
    ],
    login: ['ИНН + пароль + ПИН.'],
    screens: [
      { name: 'Личная карточка', purpose: 'Собственный профиль (после логина)' },
      { name: 'Прокторинг-сессии', purpose: 'Запускаются из TestTakingPage с включённым прокторингом' },
    ],
    scenarios: [
      {
        title: 'Запуск прокторинг-сессии',
        steps: [
          'Студент начинает экзамен с включённым прокторингом.',
          'Проктор подключается — видеопоток + экран.',
          'Алерты в реальном времени (взгляд, голос, окна).',
          'Ручная пометка «Подозрение» → попадает в журнал.',
          'Журнал передаётся методисту/декану.',
        ],
      },
    ],
    faq: [
      { q: 'Сколько студентов один проктор может вести', a: 'Не более 8 одновременно (рекомендация).' },
    ],
  },

  // ───────── Управление ─────────

  rectorate: {
    purpose: [
      'Все админ-разделы методиста, декана, ОК',
      'Единая админ-панель (12 табов)',
      'KPI для ECAQA / IAAR',
      'Реестр внешних систем (Tunduk, ЕГЭ, ЦОР, Антиплагиат, банки, СЭД)',
      'Общая статистика по университету',
    ],
    login: ['ИНН + пароль + ПИН + 2FA ОБЯЗАТЕЛЬНА.'],
    screens: [
      { name: 'Админ-панель', purpose: '12 табов: дашборд, пользователи, роли, ...' },
      { name: 'Аккредитация', purpose: 'KPI для ECAQA/IAAR' },
      { name: 'Интеграции', purpose: 'Внешние системы' },
    ],
    scenarios: [
      {
        title: 'Подготовка к аккредитации',
        steps: [
          '/reports/accreditation/ → выбор года.',
          'Сводка: ППС, остепенённость, контингент, выпуск, УМК.',
          'Экспорт в формате ECAQA/IAAR.',
        ],
      },
    ],
    faq: [
      { q: 'Кто может выпустить приказ за ректора', a: 'Только сам ректор (по подписи) либо проректор по соответствующей доверенности.' },
    ],
  },

  sysadmin: {
    purpose: [
      'Документация API (только sysadmin)',
      'Аудит-журнал — все действия пользователей',
      'Управление интеграциями (создание, изменение, проверка)',
      '/sadmincsv/ (Django Admin) — низкоуровневое управление БД',
      'Каталог Сервисов и их % готовности',
      'Управление пользователями: блокировка, разблокировка, сброс пароля и ПИНа',
      'Управление ролями: создание системных ролей, привязка прав',
    ],
    login: ['ИНН + пароль + ПИН + 2FA ОБЯЗАТЕЛЬНА.'],
    screens: [
      { name: 'API документация', purpose: 'OpenAPI / Swagger UI' },
      { name: 'Аудит-журнал', purpose: 'Все действия пользователей' },
      { name: 'Сервисы', purpose: 'Каталог и % готовности компонентов' },
      { name: 'Интеграции', purpose: 'Управление внешними системами' },
    ],
    scenarios: [
      {
        title: 'Выдать роль преподавателю',
        steps: [
          '/admin/ → «Пользователи» → найти по ФИО / ИНН.',
          'Открыть карточку → таб «Роли».',
          '«Выдать роль» → выбрать teacher (или curator, head_of_department).',
          'Подтвердить ПИН-кодом → запись попадает в аудит.',
        ],
      },
    ],
    faq: [
      { q: 'Где документация API', a: '/help/api/ — Swagger UI с полной OpenAPI 3.0 схемой.' },
    ],
  },

  // ───────── Внешние ─────────

  parent: {
    purpose: [
      'Отслеживать успеваемость своего ребёнка-студента',
      'Видеть оценки и посещаемость (через ?for=child)',
      'Просмотр библиотеки',
    ],
    login: [
      'Регистрация: родитель привязывается к студенту через ОК с проверкой документов (свидетельство о рождении / опекунство).',
      'После привязки — ИНН + пароль.',
    ],
    screens: [
      { name: 'Главная', purpose: 'Сводка по ребёнку' },
      { name: 'Успеваемость ребёнка', purpose: '/gradebook/?for=child' },
    ],
    scenarios: [
      {
        title: 'Посмотреть оценки ребёнка',
        steps: [
          'Главная → виджет «Мой ребёнок».',
          'Клик → /gradebook/?for=child.',
        ],
      },
    ],
    faq: [
      { q: 'Не вижу ребёнка в системе', a: 'Привязка не выполнена. Подайте документы в ОК.' },
    ],
  },

  applicant: {
    purpose: [
      'Подача документов на поступление',
      'Загрузка сертификатов ОРТ/ЕГЭ, аттестата, фото, паспорта',
      'Выбор направлений (до 5)',
      'Отслеживание статуса конкурса',
      'Запись на ДВИ (дополнительные внутренние испытания)',
    ],
    login: [
      'Регистрация по e-mail. ИНН добавляется при подаче документов.',
      'После зачисления учётка преобразуется в `student` (с сохранением ИНН и e-mail).',
    ],
    screens: [
      { name: 'Портал абитуриента', purpose: 'В разработке (запланирован)' },
      { name: 'Главная', purpose: 'Сводка статуса заявления' },
      { name: 'Помощь', purpose: 'Контакты приёмной комиссии' },
    ],
    scenarios: [
      {
        title: 'Подача документов (план до запуска портала)',
        steps: [
          'Лично в приёмную комиссию ЧУ — приносите аттестат, ОРТ, паспорт, 4 фото 3×4.',
          'Сотрудник создаёт вашу учётку и выбирает направления.',
          'После зачисления получаете SMS/e-mail с логином и паролем.',
        ],
      },
    ],
    faq: [
      { q: 'Когда заработает портал', a: 'Воронка приёмной кампании в разработке. Пока подача документов — лично в приёмную комиссию.' },
      { q: 'Сколько направлений можно выбрать', a: 'До 5. Приоритеты выставляются от 1 до 5.' },
      { q: 'Когда оглашают результаты', a: 'Расписание публикуется на сайте ЧУ за 2 недели до начала приёма.' },
    ],
  },

  // ───────── Спец-категории студентов ─────────

  foreign_student: {
    purpose: [
      'Все возможности студента',
      'Отдельная карточка с визовыми и миграционными данными',
      'Уведомления о приближающихся сроках (виза, регистрация)',
      'Поддержка кыргызского, русского и английского языков',
    ],
    login: ['Аналогично студенту.'],
    screens: [
      { name: 'Иностранные студенты', purpose: '№ визы, срок, миграционная карта, регистрация' },
      { name: 'Общежитие', purpose: 'Бронирование места и оплата' },
      { name: 'Остальное', purpose: 'Все стандартные разделы студента (журнал, оценки, тесты, заявления, ВКР)' },
    ],
    scenarios: [
      {
        title: 'Обновить данные визы',
        steps: [
          '/foreign-students/ → ваша карточка.',
          '«Изменить» → новый № визы и срок.',
          'Прикрепить скан → подтвердить ПИН.',
          'Деканат и ОК автоматически уведомлены.',
        ],
      },
    ],
    faq: [
      { q: 'Когда продлевать визу', a: 'Система пришлёт уведомление за 30 дней до окончания.' },
      { q: 'На каком языке заполнять документы', a: 'Любой из трёх (ru/ky/en). Интерфейс переключается в топбаре.' },
    ],
  },

  postgrad: {
    purpose: [
      'Все возможности студента',
      'Карточка магистратуры/аспирантуры',
      'Этап (магистрант/аспирант), научный руководитель, тема, год защиты',
      'Учёт собственных публикаций',
      'Магистерская диссертация (для магистрантов)',
    ],
    login: ['Аналогично студенту.'],
    screens: [
      { name: 'Аспирантура', purpose: 'Карточка с этапом, руководителем, темой' },
      { name: 'Наука', purpose: 'Свои публикации (Scopus/WoS/РИНЦ)' },
      { name: 'ВКР', purpose: 'Магистерская диссертация (для магистрантов)' },
    ],
    scenarios: [
      {
        title: 'Утвердить тему диссертации',
        steps: [
          '/postgrad/ → ваша карточка → «Тема».',
          'Опишите тему, прикрепите план-проспект.',
          'Отправить руководителю на согласование.',
          'После согласования — на утверждение учёным советом.',
        ],
      },
      {
        title: 'Зарегистрировать публикацию',
        steps: [
          '/science/ → «Публикации» → «Добавить».',
          'Авторы, название, журнал, год, ISSN/ISBN, DOI.',
          'Прикрепите PDF.',
          'Учитывается в личном рейтинге и отчётах кафедры.',
        ],
      },
    ],
    faq: [
      { q: 'Можно ли иметь двух руководителей', a: 'Да, для интердисциплинарных тем. Указывается основной + соруководитель.' },
      { q: 'Срок защиты диссертации', a: 'Магистры — 2 года, аспиранты — 3 (очная) / 4 (заочная) с момента зачисления.' },
    ],
  },

  dpo_student: {
    purpose: [
      'Курс ДПО: название, направление, объём в часах',
      'Стоимость, статус оплаты',
      'Расписание занятий',
      'Сертификат (по завершению)',
      'Доступ к /distance/ для онлайн-курсов',
    ],
    login: ['Регистрация при записи на курс. ИНН + пароль.'],
    screens: [
      { name: 'ДПО', purpose: 'Карточка курса' },
      { name: 'Дистанционное обучение', purpose: 'Видеолекции и виртуальные классы' },
      { name: 'Расписание', purpose: 'Расписание ваших занятий' },
    ],
    scenarios: [
      {
        title: 'Получить сертификат после курса',
        steps: [
          'После завершения курса проктор/преподаватель проставляет «Завершено».',
          'Бухгалтерия проверяет полную оплату.',
          '/dpo/ → «Сертификат» → скачать PDF с QR-подписью.',
        ],
      },
    ],
    faq: [
      { q: 'Можно ли выбрать несколько курсов', a: 'Да, без ограничений по числу одновременных курсов.' },
      { q: 'Сертификат принимают другие вузы', a: 'Это документ ЧУ. Признание зависит от принимающей стороны.' },
    ],
  },
}
